import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import { Button } from '@blueprintjs/core';
import sub from 'date-fns/sub';
// Components
import Panel from '../../../components/panel/Panel';
import GenericSelector, {
  IValueKey,
} from 'src/components/generic-selector/GenericSelector';
import TimeFrameSelect from './time-frame-select/TimeFrameSelect';
import ActivityBarChart from './charts/ActivityBarChart';
import ActionsPieChart from './charts/ActionsPieChart';
import ProcessingTimeChart from './charts/ProcessingTimeChart';
import BackupIcon from 'src/components/icons/BackupIcon';
// Actions
import { getData } from '../../../redux/slices/dashboard/charts/thunk';
import { reset as resetState } from '../../../redux/slices/dashboard/charts/chartsSlice';
// Selectors
import { selectConnectorState } from '../../../redux/slices/dashboard/connector/selectors';
import { selectDashboardCharts } from '../../../redux/slices/dashboard/charts/selectors';
// Utils
import { filterByDateRange } from './charts/utils';
import useConnectorActions from '../utils/useConnectorActions';
import useTaskStates from '../utils/useTaskStates';
// Types
import { RequestStatus } from '../../../redux/slices/types';
import { IDateRange } from 'src/redux/slices/dashboard/charts/types';
import './styles.scss';

const options = [
  { hours: 1 },
  { hours: 6 },
  { hours: 12 },
  { days: 1 },
  { weeks: 1 },
  { months: 1 },
];

const DashboardCharts: FC = () => {
  const { data: connector } = useSelector(selectConnectorState);
  const { data, loading } = useSelector(selectDashboardCharts);

  const { actions } = useConnectorActions();
  const states = useTaskStates();

  const [dateRange, setDateRange] = useState<IDateRange>([
    sub(new Date(), { hours: 1 }),
    new Date(),
  ]);

  const [selectedAction, setSelectedAction] = useState<IValueKey | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<IValueKey | undefined>(undefined);

  const barsData = useMemo(
    () =>
      filterByDateRange(data.logs, dateRange)
        .filter((log) =>
          selectedAction?.Value ? log.action === selectedAction.Value : true
        )
        .filter((log) => (selectedState?.Key ? log.state === selectedState.Key : true)),
    [data, dateRange, selectedAction, selectedState]
  );

  const isLoading = loading === RequestStatus.pending;
  const [startTime] = dateRange;

  const { t } = useTranslation(['dashboard']);
  const dispatch = useDispatch();

  // Handlers
  const handleActionSelect = ([value]: IValueKey[] = []) => {
    setSelectedAction(value);
  };

  const handleDateSelect = (date) => {
    setDateRange(date);
  };

  const handleSateSelect = ([value]: IValueKey[] = []) => {
    setSelectedState(value);
  };

  const handleUpdate = useCallback(() => {
    if (connector?.nid) {
      dispatch(getData({ connectorId: connector.nid }));
    }
  }, [dispatch, connector]);

  useEffect(() => {
    if (connector?.nid) {
      dispatch(getData({ connectorId: connector.nid }));
    }
  }, [connector, dispatch]);

  // Component did unmount
  useEffect(
    () => () => {
      dispatch(resetState());
    },
    [dispatch]
  );

  return (
    <div className="dashboard-charts">
      <Row className="diagrams-panel">
        <Panel>
          <TimeFrameSelect
            onChange={handleDateSelect}
            selectedDate={startTime ?? undefined}
            options={options}
          />
          <GenericSelector
            selectorText={t('inputs.All Actions')}
            isMultiSelector={false}
            onValueChanged={handleActionSelect}
            items={actions}
            disabled={!actions?.length}
            selectedItem={selectedAction ? [selectedAction] : undefined}
          />
          <GenericSelector
            selectorText={t('inputs.All States')}
            isMultiSelector={false}
            onValueChanged={handleSateSelect}
            items={states}
            selectedItem={selectedState ? [selectedState] : undefined}
          />
          <Button
            className="action-button"
            icon={<BackupIcon />}
            onClick={handleUpdate}
          />
        </Panel>
      </Row>
      <Row className="dashboard-charts-wrapper">
        <Col md={4}>
          <ActivityBarChart isLoading={isLoading} data={barsData} dateRange={dateRange} />
        </Col>
        <Col md={4}>
          <ActionsPieChart isLoading={isLoading} data={barsData} />
        </Col>

        <Col md={4}>
          <ProcessingTimeChart
            isLoading={isLoading}
            data={barsData}
            dateRange={dateRange}
          />
        </Col>
      </Row>
    </div>
  );
};

export const mapDashboardChartsProps = (props) => props;

export default DashboardCharts;
